'use client';

import Script from 'next/script';
import { AdTagGate } from '@/components/privacy/AdTagGate';
import { OPT_OUT_INLINE } from '@/lib/privacy-optout';
import { MortgageMarketingDemoFormV3 } from './MortgageMarketingDemoFormV3';
import { DemoBaseContext } from './demo-base';
import { VariantMarker } from './VariantMarker';
import ScrapeDebugPanel from '@/components/debug/ScrapeDebugPanel';

/**
 * The new demo-request experience as a PAGE: the same page-level tracking tags the production page carries (copied verbatim
 * from src/app/mortgage-lead-generation-demo/page.tsx), the variant marker, and the form. No site header/footer: the form is
 * full-screen. `base` is the public URL it runs on; `source` is its variant ('pricing' on /pricing).
 */
export function DemoFormPage({ base, source, debug }: { base: string; source: string; debug?: boolean }) {
  return (
    <DemoBaseContext.Provider value={{ path: base, source }}>
      <VariantMarker variant="b" />
      {/* LinkedIn Insight Tag - Form page only (not global) */}
      <AdTagGate>
        <Script id="linkedin-insight" strategy="afterInteractive">
          {`
            _linkedin_partner_id = "7921932";
            window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
            window._linkedin_data_partner_ids.push(_linkedin_partner_id);
          `}
        </Script>
      </AdTagGate>
      <AdTagGate>
        <Script
          id="linkedin-insight-script"
          strategy="afterInteractive"
          src="https://snap.licdn.com/li.lms-analytics/insight.min.js"
        />
      </AdTagGate>

      {/* Google Analytics - Form page only */}
      <Script
        id="ga-form-page"
        strategy="afterInteractive"
        src="https://www.googletagmanager.com/gtag/js?id=G-REKRRCFF1H"
      />
      <Script id="ga-form-config" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          if (${OPT_OUT_INLINE}) { gtag('consent', 'default', { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied', analytics_storage: 'granted' }); gtag('set', 'allow_google_signals', false); gtag('set', 'allow_ad_personalization_signals', false); }
          gtag('js', new Date());
          gtag('config', 'G-REKRRCFF1H');
        `}
      </Script>

      {/* Facebook Pixel - Form page only */}
      <AdTagGate>
        <Script id="fb-pixel-form-page" strategy="afterInteractive">
          {`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '206443979084112');
            fbq('track', 'PageView');
          `}
        </Script>
      </AdTagGate>

      {/* HubSpot - Form page only */}
      <AdTagGate>
        <Script
          id="hs-script-form-page"
          strategy="afterInteractive"
          src="https://js.hs-scripts.com/7578384.js"
        />
      </AdTagGate>

      <main>
        <MortgageMarketingDemoFormV3 />
      </main>

      {/* Debug Panel - Only visible with ?debug=true */}
      {debug && <ScrapeDebugPanel />}
    </DemoBaseContext.Provider>
  );
}
